// ═══════════════════════════════════════════════════════════════════════════════
// Main app scroll helpers — inner scroll (PWA) vs document scroll (browser tab)
// ═══════════════════════════════════════════════════════════════════════════════

import { isRunningAsPwa } from './pwaRuntime';

export const MAIN_APP_SCROLL_SELECTOR = '[data-main-app-scroll]';

const resolveMainScrollElement = (): HTMLElement | null => {
  const el = document.querySelector(MAIN_APP_SCROLL_SELECTOR);
  if (el instanceof HTMLElement) {
    return el;
  }
  return null;
};

/** Current vertical scroll position for the main app content region. */
export const getMainAppScrollTop = (): number => {
  if (isRunningAsPwa()) {
    return resolveMainScrollElement()?.scrollTop ?? 0;
  }
  return window.scrollY;
};

/** Set vertical scroll position (inner container or window). */
export const setMainAppScrollTop = (top: number): void => {
  const safeTop = Math.max(0, Math.round(top));
  if (isRunningAsPwa()) {
    const el = resolveMainScrollElement();
    if (el) {
      el.scrollTop = safeTop;
    }
    return;
  }
  window.scrollTo({ top: safeTop, left: 0, behavior: 'auto' });
};

/** Scroll so `anchor` aligns below the main app top inset (works in both scroll modes). */
export const scrollMainAppAnchorIntoView = (anchor: HTMLElement, marginTop = 12): void => {
  if (isRunningAsPwa()) {
    const mainScroll = resolveMainScrollElement();
    if (mainScroll?.contains(anchor)) {
      const elRect = anchor.getBoundingClientRect();
      const parentRect = mainScroll.getBoundingClientRect();
      const targetTop = elRect.top - parentRect.top + mainScroll.scrollTop - marginTop;
      mainScroll.scrollTop = Math.max(0, Math.round(targetTop));
      return;
    }
  }
  const targetTop = anchor.getBoundingClientRect().top + window.scrollY - marginTop;
  window.scrollTo({ top: Math.max(0, Math.round(targetTop)), left: 0, behavior: 'auto' });
};
